import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import logisticsApi from '@utils/logisticsApi';
import { useI18n } from '@/i18n';
import PageHeader from '@components/PageHeader';
import { IconCheckSquare } from '@components/icons';

export default function Confirmations() {
  const { t, lang } = useI18n();
  const [orders,  setOrders]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId,  setBusyId]  = useState(null);
  const [error,   setError]   = useState(null);

  const load = useCallback(() => {
    logisticsApi.get('/purchase-orders/pending-confirmation')
      .then(({ data }) => setOrders(data.purchaseOrders || []))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function decide(id, accepted) {
    let notes;
    if (!accepted) notes = window.prompt(t('logisticsConfirmations.rejectPrompt')) || '';
    setBusyId(id);
    setError(null);
    try {
      await logisticsApi.post(`/purchase-orders/${id}/confirmation`, { accepted, notes });
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <PageHeader title={t('logisticsConfirmations.title')} subtitle={t('logisticsConfirmations.subtitle')} />
      <div className="px-5 md:px-0 pb-6">
        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

        {loading ? (
          <div className="card p-8 text-center text-gray-400">{t('common.loading')}</div>
        ) : orders.length === 0 ? (
          <div className="card p-10 text-center">
            <div className="mx-auto w-12 h-12 grid place-items-center rounded-xl bg-gray-100 text-gray-400 mb-3">
              <IconCheckSquare size={24} />
            </div>
            <p className="text-sm text-gray-500">{t('logisticsConfirmations.empty')}</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {orders.map((po, i) => (
              <motion.div key={po.id}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.04 }}
                className="card p-4">
                <div className="flex items-center gap-4">
                  <span className="shrink-0 grid place-items-center w-10 h-10 rounded-xl bg-glk-blue/10 text-glk-blue">
                    <IconCheckSquare size={20} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-glk-dark truncate">{po.po_number}</p>
                    <p className="text-xs text-gray-400 truncate">
                      {po.destination_station} · {new Date(po.updated_at).toLocaleDateString(lang === 'en' ? 'en-GB' : 'pt-PT')}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    disabled={busyId === po.id}
                    onClick={() => decide(po.id, true)}
                    className="flex-1 rounded-lg bg-glk-green text-white text-sm font-medium py-2 disabled:opacity-50"
                  >
                    {t('logisticsConfirmations.accept')}
                  </button>
                  <button
                    disabled={busyId === po.id}
                    onClick={() => decide(po.id, false)}
                    className="flex-1 rounded-lg border border-red-300 text-red-700 text-sm font-medium py-2 disabled:opacity-50"
                  >
                    {t('logisticsConfirmations.reject')}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
